import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { PlanCheckoutModal } from './PlanCheckoutModal';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';
import type { PortalSubscription } from 'modules/portal-plans/api';

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export interface PortalRenewalBannerProps {
  subscription?: PortalSubscription | null;
  onRenew?: () => void;
  className?: string;
}

export function PortalRenewalBanner({
  subscription: subscriptionProp,
  onRenew,
  className = '',
}: PortalRenewalBannerProps) {
  const { data: hookSubscription } = usePortalSubscription();
  const subscription = subscriptionProp !== undefined ? subscriptionProp : hookSubscription;
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !subscription || !subscription.isRenewalDue) {
    return null;
  }

  const isExpired = subscription.isExpired || !subscription.isActive;
  const days = subscription.daysRemaining ?? 0;
  const planName = subscription.planName || 'Portal';

  const handleRenewClick = () => {
    if (onRenew) {
      onRenew();
    } else {
      setInternalModalOpen(true);
    }
  };

  return (
    <>
      <section
        aria-label="Tarifni yangilash eslatmasi"
        className={`relative flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between transition-all ${
          isExpired
            ? 'border-destructive/30 bg-destructive/10 text-destructive-foreground dark:bg-destructive/20'
            : 'border-amber-500/30 bg-amber-500/10 text-foreground dark:bg-amber-500/15'
        } ${className}`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
              isExpired
                ? 'bg-destructive/20 text-destructive dark:text-red-400'
                : 'bg-amber-500/20 text-amber-700 dark:text-amber-400'
            }`}
          >
            {isExpired ? (
              <AlertTriangleIcon className="h-5 w-5" />
            ) : (
              <ClockIcon className="h-5 w-5" />
            )}
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-sm sm:text-base text-foreground">
                {isExpired
                  ? `${planName} obunangiz muddati tugagan`
                  : `${planName} obunasi muddati tugashiga ${
                      days === 0 ? 'bugun' : `${days} kun`
                    } qoldi`}
              </span>
              <Badge
                variant="outline"
                className={`text-[11px] font-medium ${
                  isExpired
                    ? 'border-destructive/40 text-destructive dark:text-red-400'
                    : 'border-amber-500/40 text-amber-700 dark:text-amber-400'
                }`}
              >
                {isExpired ? 'Muddati tugagan' : 'Yangilash vaqti'}
              </Badge>
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-relaxed">
              {isExpired
                ? 'Barcha mock testlar, batafsil tahlillar va yangi topshiriqlardan to‘liq foydalanishni davom ettirish uchun tarifingizni yangilang.'
                : 'Obuna tugagach testlardan foydalanish to‘xtab qolmasligi uchun hozirdanoq obunangizni yangilab qo‘yishingiz mumkin.'}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 pt-1 sm:pt-0">
          <Button
            size="sm"
            className="w-full sm:w-auto font-semibold shadow-sm"
            onClick={handleRenewClick}
          >
            Tarifni yangilash
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setDismissed(true)}
          >
            Keyinroq
          </Button>
        </div>
      </section>

      {internalModalOpen && (
        <PlanCheckoutModal
          open={internalModalOpen}
          onOpenChange={setInternalModalOpen}
          defaultPlan={
            subscription.planKey === 'START' || subscription.planKey === 'PREMIUM'
              ? subscription.planKey
              : 'PRO'
          }
          subscription={subscription}
        />
      )}
    </>
  );
}

export default PortalRenewalBanner;
